import { MeshoptSimplifier } from 'meshoptimizer';

const FIFTY_MB = 50 * 1024 * 1024;
// Aim for 40 MB after simplification to give a safety margin below the 50 MB limit.
const TARGET_SIZE = 40 * 1024 * 1024;
// Never decimate past 70% triangle reduction to preserve clinically relevant geometry.
const MIN_SIMPLIFICATION_RATIO = 0.3;

// ---------------------------------------------------------------------------
// ASCII STL helpers (lossless size reduction)
// ---------------------------------------------------------------------------

// Detects whether a buffer is an ASCII or binary STL file.
// Binary STL: 80-byte header + uint32 triangle count; file size must equal 84 + count * 50.
function detectStlType(buffer) {
    if (buffer.byteLength < 84) return 'ascii';
    const triangleCount = new DataView(buffer).getUint32(80, true);
    if (84 + triangleCount * 50 === buffer.byteLength) return 'binary';
    const header = new TextDecoder().decode(new Uint8Array(buffer, 0, 256));
    return header.trimStart().toLowerCase().startsWith('solid') ? 'ascii' : 'binary';
}

// Converts ASCII STL text to a binary STL ArrayBuffer (lossless, 4-5× smaller).
// Output is a fully valid binary STL — no format change from SmileyNova's perspective.
function convertAsciiStlToBinary(text) {
    const pattern =
        /facet\s+normal\s+([\S]+)\s+([\S]+)\s+([\S]+)\s+outer\s+loop\s+vertex\s+([\S]+)\s+([\S]+)\s+([\S]+)\s+vertex\s+([\S]+)\s+([\S]+)\s+([\S]+)\s+vertex\s+([\S]+)\s+([\S]+)\s+([\S]+)\s+endloop\s+endfacet/gi;

    const floatRows = [];
    let m;
    while ((m = pattern.exec(text)) !== null) {
        floatRows.push([
            parseFloat(m[1]),  parseFloat(m[2]),  parseFloat(m[3]),  // normal
            parseFloat(m[4]),  parseFloat(m[5]),  parseFloat(m[6]),  // vertex 1
            parseFloat(m[7]),  parseFloat(m[8]),  parseFloat(m[9]),  // vertex 2
            parseFloat(m[10]), parseFloat(m[11]), parseFloat(m[12]), // vertex 3
        ]);
    }

    // Binary STL: 80-byte header | uint32 count | (12 floats + uint16) × count
    const buffer = new ArrayBuffer(84 + floatRows.length * 50);
    const view = new DataView(buffer);
    view.setUint32(80, floatRows.length, true);
    let offset = 84;
    for (const row of floatRows) {
        for (let i = 0; i < 12; i++) {
            view.setFloat32(offset, row[i], true);
            offset += 4;
        }
        view.setUint16(offset, 0, true); // attribute byte count
        offset += 2;
    }
    return buffer;
}

// ---------------------------------------------------------------------------
// Mesh simplification helpers (lossy — preserves valid STL format)
// ---------------------------------------------------------------------------

// Extract all vertex positions from a binary STL as a flat Float32Array (triangle soup).
function parseBinaryStlPositions(buffer) {
    const view = new DataView(buffer);
    const triangleCount = view.getUint32(80, true);
    // 9 floats per triangle (3 vertices × 3 coords); normals are skipped.
    const positions = new Float32Array(triangleCount * 9);
    let inOff = 84;
    let outIdx = 0;
    for (let i = 0; i < triangleCount; i++) {
        inOff += 12; // skip face normal
        for (let v = 0; v < 3; v++) {
            positions[outIdx++] = view.getFloat32(inOff, true); inOff += 4;
            positions[outIdx++] = view.getFloat32(inOff, true); inOff += 4;
            positions[outIdx++] = view.getFloat32(inOff, true); inOff += 4;
        }
        inOff += 2; // skip attribute byte count
    }
    return { positions, triangleCount };
}

// Serialise an indexed mesh (positions + indices) back to a binary STL ArrayBuffer.
// Face normals are recomputed from each triangle's edges.
function writeBinaryStl(positions, indices) {
    const triCount = indices.length / 3;
    const buffer = new ArrayBuffer(84 + triCount * 50);
    const view = new DataView(buffer);
    view.setUint32(80, triCount, true);

    let offset = 84;
    for (let i = 0; i < indices.length; i += 3) {
        const a = indices[i], b = indices[i + 1], c = indices[i + 2];
        const ax = positions[a * 3], ay = positions[a * 3 + 1], az = positions[a * 3 + 2];
        const bx = positions[b * 3], by = positions[b * 3 + 1], bz = positions[b * 3 + 2];
        const cx = positions[c * 3], cy = positions[c * 3 + 1], cz = positions[c * 3 + 2];

        // Face normal via cross product of two edges
        const ex = bx - ax, ey = by - ay, ez = bz - az;
        const fx = cx - ax, fy = cy - ay, fz = cz - az;
        const nx = ey * fz - ez * fy;
        const ny = ez * fx - ex * fz;
        const nz = ex * fy - ey * fx;
        const len = Math.sqrt(nx * nx + ny * ny + nz * nz) || 1;

        view.setFloat32(offset, nx / len, true); offset += 4;
        view.setFloat32(offset, ny / len, true); offset += 4;
        view.setFloat32(offset, nz / len, true); offset += 4;

        view.setFloat32(offset, ax, true); offset += 4;
        view.setFloat32(offset, ay, true); offset += 4;
        view.setFloat32(offset, az, true); offset += 4;
        view.setFloat32(offset, bx, true); offset += 4;
        view.setFloat32(offset, by, true); offset += 4;
        view.setFloat32(offset, bz, true); offset += 4;
        view.setFloat32(offset, cx, true); offset += 4;
        view.setFloat32(offset, cy, true); offset += 4;
        view.setFloat32(offset, cz, true); offset += 4;

        view.setUint16(offset, 0, true); offset += 2;
    }
    return buffer;
}

// Simplify a binary STL File using meshoptimizer's QEM decimation.
// The output is a smaller but geometrically valid binary STL file.
async function simplifyStl(file) {
    await MeshoptSimplifier.ready;

    const buffer = await file.arrayBuffer();
    const { positions, triangleCount } = parseBinaryStlPositions(buffer);

    // Build an indexed mesh from the STL's triangle soup.
    // generatePositionRemap deduplicates vertices by exact float-bit equality.
    const remap = MeshoptSimplifier.generatePositionRemap(positions, 3);

    let uniqueCount = 0;
    for (let i = 0; i < remap.length; i++) {
        if (remap[i] >= uniqueCount) uniqueCount = remap[i] + 1;
    }

    const uniquePos = new Float32Array(uniqueCount * 3);
    for (let i = 0; i < remap.length; i++) {
        const ui = remap[i];
        uniquePos[ui * 3]     = positions[i * 3];
        uniquePos[ui * 3 + 1] = positions[i * 3 + 1];
        uniquePos[ui * 3 + 2] = positions[i * 3 + 2];
    }

    const indices = new Uint32Array(remap.buffer, remap.byteOffset, remap.length);

    // Compute target triangle count from desired output size.
    // Binary STL size = 84 + triCount × 50 bytes (fixed per triangle).
    const targetTriangleCount = Math.floor((TARGET_SIZE - 84) / 50);
    const ratio = Math.max(MIN_SIMPLIFICATION_RATIO, targetTriangleCount / triangleCount);
    const targetIndexCount = Math.floor(indices.length * ratio);

    const [simplifiedIndices] = MeshoptSimplifier.simplify(
        indices,
        uniquePos,
        3,
        targetIndexCount,
        0.01,       // max allowed geometric error (relative to mesh scale)
        ['LockBorder'] // preserve boundary edges to avoid open holes
    );

    const stlBuffer = writeBinaryStl(uniquePos, simplifiedIndices);
    return new File([stlBuffer], file.name, {
        type: file.type || 'application/octet-stream',
    });
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------

/**
 * Reduces the size of a 3D scan file before upload when it exceeds 50 MB.
 *
 * For STL files, applies two stages in order:
 *  1. ASCII → binary conversion (lossless, 4-5× smaller, valid STL output).
 *  2. QEM mesh simplification via meshoptimizer (lossy but geometrically valid,
 *     targets ~40 MB output to stay safely under the 50 MB SmileyNova limit).
 *
 * PLY and OBJ files over 50 MB are not supported for compression — tooLarge is
 * set to true and the user is asked to re-export at a lower resolution.
 *
 * @param {File} file
 * @returns {Promise<{ file: File, tooLarge: boolean }>}
 */
export async function compressIfNeeded(file) {
    if (file.size <= FIFTY_MB) {
        return { file, tooLarge: false };
    }

    const ext = file.name.split('.').pop().toLowerCase();

    if (ext === 'stl') {
        const buffer = await file.arrayBuffer();
        let binaryFile = file;

        // Stage 1: lossless ASCII → binary conversion
        if (detectStlType(buffer) === 'ascii') {
            const text = new TextDecoder().decode(buffer);
            const binaryBuffer = convertAsciiStlToBinary(text);
            binaryFile = new File([binaryBuffer], file.name, {
                type: file.type || 'application/octet-stream',
            });
            if (binaryFile.size <= FIFTY_MB) {
                return { file: binaryFile, tooLarge: false };
            }
        }

        // Stage 2: lossy QEM mesh simplification
        const simplified = await simplifyStl(binaryFile);
        return {
            file: simplified,
            tooLarge: simplified.size > FIFTY_MB,
            reason: 'stl_size_limit',
        };
    }

    // PLY and OBJ: no compression path that keeps a valid, parseable 3D format.
    return { file, tooLarge: true, reason: 'format_unsupported' };
}
